import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { LocaleService } from '@sm360/widget-commons-core-services';
import { AnalyticsService, TRACKING_TAGS as TAGS } from '@sm360/widget-commons-general';
import { FIELD_LENGTH, ValidationService } from '@sm360/widget-commons-forms';
import { InputNumber, Input } from '@sm360/ui-library';
import WEB_CSS_CLASSNAMES from 'trade-in-360/constants/webCssClassnames.js';
import ActionButton from '../../Components/ActionButton/ActionButton.jsx';
import NewTradeInContext from '../../../../NewTradeInContext.js';
import './VehicleBasicInfo.scss';

function VehicleBasicInfo({ tradeInVehicle, onFormSubmit, onNextStep }) {
    const { fieldsLocked, isViewOnly } = useContext(NewTradeInContext);

    const [inputs, setInputs] = useState(tradeInVehicle);
    const disabled = fieldsLocked || isViewOnly;

    useEffect(
        () => {
            setInputs(tradeInVehicle);
        },
        [tradeInVehicle]
    );

    let inputValidation = {
        make: false,
        model: false,
        year: false,
        trim: false,
        exteriorColor: false,
        interiorColor: false,
        odometer: false,
        vin: false,
    };

    const checkforErrors = ({ errors, name }) => {
        inputValidation = { ...inputValidation, [name]: !errors.length > 0 };
    };

    function handleChange(name) {
        return (value) => {
            let v = value;
            if (name === 'vin') v = value.toUpperCase();
            setInputs((values) => ({ ...values, [name]: v }));
        };
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_NEXT_BTN());
        if (disabled) return onNextStep();
        const isFormValid = ValidationService.validateForm(inputValidation);
        if (!isFormValid) {
            return false;
        }
        return onFormSubmit({ tradeInVehicle, ...inputs });
    };

    const inputFields = {
        year: (
            <InputNumber
                id="year"
                label={LocaleService.t('TRADE_IN.YEAR')}
                classNames={['styled-placeholder quarter']}
                name="year"
                value={inputs.year}
                onChange={handleChange('year')}
                onError={checkforErrors}
                maxLength={FIELD_LENGTH.YEAR_MAX}
                min={1900}
                max={3000}
                customMessage={{
                    number: LocaleService.t('TRADE_IN.MODEL_YEAR.ERROR', {
                        min: 1900,
                    }),
                    required: LocaleService.t('COMMONS_FORMS.ERRORS.YEAR'),
                }}
                required
                disabled={disabled}
                withErrorTooltip={false}
                icon="icon"
            />
        ),

        make: (
            <Input
                id="make"
                label={LocaleService.t('TRADE_IN.MAKE')}
                name="make"
                value={inputs.make || ''}
                type="text"
                onChange={handleChange('make')}
                onError={checkforErrors}
                maxChar={FIELD_LENGTH.MAKE_MAX}
                disabled={disabled}
                customMessage={{
                    required: LocaleService.t('COMMONS_FORMS.ERRORS.MAKE'),
                    maxChar: LocaleService.t('TRADE_IN.MAX_CHAR', {
                        max: FIELD_LENGTH.MAKE_MAX,
                    }),
                }}
                withErrorTooltip={false}
                required
                icon="icon"
            />
        ),
        model: (
            <Input
                id="model"
                label={LocaleService.t('TRADE_IN.MODEL')}
                customMessage={{
                    required: LocaleService.t('COMMONS_FORMS.ERRORS.MODEL'),
                    maxChar: LocaleService.t('TRADE_IN.MAX_CHAR', {
                        max: FIELD_LENGTH.MODEL_MAX,
                    }),
                }}
                name="model"
                type="text"
                value={inputs.model || ''}
                onChange={handleChange('model')}
                onError={checkforErrors}
                maxChar={FIELD_LENGTH.MODEL_MAX}
                withErrorTooltip={false}
                disabled={disabled}
                required
                icon="icon"
            />
        ),
        trim: (
            <Input
                id="trim"
                label={LocaleService.t('TRADE_IN.TRIM')}
                customMessage={{
                    required: LocaleService.t('COMMONS_FORMS.ERRORS.TRIM'),
                    maxChar: LocaleService.t('TRADE_IN.MAX_CHAR', {
                        max: FIELD_LENGTH.TRIM_MAX,
                    }),
                }}
                name="trim"
                type="text"
                value={inputs.trim || ''}
                onChange={handleChange('trim')}
                onError={checkforErrors}
                maxChar={FIELD_LENGTH.TRIM_MAX}
                disabled={disabled}
                withErrorTooltip={false}
                required
                icon="icon"
            />
        ),
        exteriorColor: (
            <Input
                id="exteriorColor"
                customMessage={{
                    required: LocaleService.t('COMMONS_FORMS.ERRORS.COLOR'),
                    maxChar: LocaleService.t('TRADE_IN.MAX_CHAR', {
                        max: FIELD_LENGTH.COLOR_MAX,
                    }),
                }}
                name="exteriorColor"
                type="text"
                value={inputs?.exteriorColor?.description || ''}
                onChange={(description) => handleChange('exteriorColor')({ description })}
                onError={checkforErrors}
                maxChar={FIELD_LENGTH.COLOR_MAX}
                disabled={disabled}
                withErrorTooltip={false}
                required
                icon="icon"
            />
        ),
        interiorColor: (
            <Input
                id="interiorColor"
                customMessage={{
                    maxChar: LocaleService.t('TRADE_IN.MAX_CHAR', {
                        max: FIELD_LENGTH.COLOR_MAX,
                    }),
                }}
                name="interiorColor"
                type="text"
                value={inputs?.interiorColor?.description || ''}
                onChange={(description) => handleChange('interiorColor')({ description })}
                onError={checkforErrors}
                maxChar={FIELD_LENGTH.COLOR_MAX}
                disabled={disabled}
                withErrorTooltip={false}
                icon={inputs?.interiorColor?.description?.length > 0 && 'icon'}
            />
        ),
        odometer: (
            <InputNumber
                id="odometer"
                classNames={['styled-placeholder quarter']}
                locale={LocaleService.getSimpleLocale()}
                name="odometer"
                value={inputs.odometer}
                onChange={handleChange('odometer')}
                onError={checkforErrors}
                maxLength={FIELD_LENGTH.ODOMETER_MAX}
                min={0}
                max={999999}
                customMessage={{
                    required: LocaleService.t('COMMONS_FORMS.ERRORS.MILLEAGE'),
                    number: LocaleService.t('TRADE_IN.ODOMETER.ERROR', {
                        min: 0,
                        max: 999999,
                    }),
                }}
                required
                disabled={disabled}
                withErrorTooltip={false}
                icon="icon"
            />
        ),
        vin: (
            <Input
                id="vin"
                customValidation={ValidationService.getVinRangeRegex()}
                customMessage={{ customValidation: LocaleService.t('COMMONS_FORMS.ERRORS.VIN') }}
                name="vin"
                type="text"
                value={inputs?.vin || ''}
                onChange={handleChange('vin')}
                onError={checkforErrors}
                disabled={disabled}
                withErrorTooltip={false}
                icon={inputs?.vin?.length > 0 && 'icon'}
            />
        ),
    };

    return (
        <div>
            <form className="trade-in-basic-form" onSubmit={handleSubmit}>
                <section className="clearfix step-section">
                    <div className={WEB_CSS_CLASSNAMES.SUBHEADING1}>
                        {LocaleService.t('TRADE_IN.YOUR_VEHICLE_INFORMATION')}
                    </div>
                    <div className="trade-in-form-vehicle-info">
                        <div className="clearfix">{inputFields.year}</div>
                        <div className="clearfix">{inputFields.make}</div>
                        <div className="clearfix">{inputFields.model}</div>
                        <div className="clearfix">{inputFields.trim}</div>
                    </div>
                    <div className={WEB_CSS_CLASSNAMES.HEADING4}>{LocaleService.t('TRADE_IN.VIN_ONLY')}</div>
                    <div className="clearfix">{inputFields.vin}</div>
                </section>
                <section className="clearfix step-section">
                    <div className={WEB_CSS_CLASSNAMES.SUBHEADING1}>{LocaleService.t('TRADE_IN.EXTERIOR_COLOR')}</div>
                    <div className="clearfix">{inputFields.exteriorColor}</div>
                </section>
                <section className="clearfix step-section">
                    <div className={WEB_CSS_CLASSNAMES.SUBHEADING1}>{LocaleService.t('TRADE_IN.INTERIOR_COLOR')}</div>
                    <div className="clearfix">{inputFields.interiorColor}</div>
                </section>
                <section className="clearfix step-section">
                    <div className={WEB_CSS_CLASSNAMES.SUBHEADING1}>{LocaleService.t('TRADE_IN.ODOMETER_READING')}</div>
                    <div className="clearfix">{inputFields.odometer}</div>
                </section>
                <section className="clearfix vehicle-buttons-container">
                    <div className="vehicle-buttons">
                        <ActionButton noPrefix noArrow disabled>
                            {LocaleService.t('TRADE_IN.BUTTONS.BACK')}
                        </ActionButton>
                        <ActionButton next noPrefix noArrow>
                            {LocaleService.t('TRADE_IN.BUTTONS.NEXT')}
                        </ActionButton>
                    </div>
                </section>
            </form>
        </div>
    );
}
VehicleBasicInfo.defaultProps = {
    tradeInVehicle: {},
};
VehicleBasicInfo.propTypes = {
    onFormSubmit: PropTypes.func.isRequired,
    tradeInVehicle: PropTypes.object,
    onNextStep: PropTypes.func.isRequired,
};
VehicleBasicInfo.displayName = 'VehicleBasicInfo';
export default VehicleBasicInfo;
